import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation, useRoutes } from "react-router-dom";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Alert,
    Snackbar,
    CircularProgress,
    Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    getChildSiteMappings,
    updateSiteMapping,
    getSitemapContent,
    uploadSitemap,
    deleteChildSiteMapping
} from "../auth/service";
import "../Sitemap/sitemap.css";
import "../Sitemap/sitemap-extra.css";


const ChildSitemap = () => {
    const navigate = useNavigate();
    const router = useNavigate()
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const sitemapUrl = searchParams.get("sitemap");
    const sourceFileLabel = location.state?.sourceFile;

    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);
    const [total, setTotal] = useState(0);

    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedChild, setSelectedChild] = useState(null);
    const [editContent, setEditContent] = useState("");

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    useEffect(() => {
        if (sitemapUrl) {
            loadChildren(page + 1, limit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sitemapUrl, page, limit]);

    const loadChildren = async (pageNum = 1, pageLimit = limit) => {
        setLoading(true);
        const response = await getChildSiteMappings(sitemapUrl, {
            page: pageNum,
            limit: pageLimit,
        });

        if (response.error) {
            showSnackbar(response.error, "error");
            setChildren([]);
        } else if (response.data && Array.isArray(response.data)) {
            setChildren(response.data);
            setTotal(response.pagination?.total ?? response.data.length);
        } else {
            setChildren([]);
        }
        setLoading(false);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file && (file.name.endsWith(".xml") || file.name.endsWith(".xml.gz"))) {
            setSelectedFile(file);
        } else {
            showSnackbar("Please upload a valid XML file", "error");
        }
    };

    const handleUploadSubmit = async () => {
        if (!selectedFile) {
            showSnackbar("Please select a file first", "error");
            return;
        }

        setLoading(true);
        const response = await uploadSitemap(selectedFile);

        if (response.error) {
            showSnackbar(response.error, "error");
        } else {
            showSnackbar("Sitemap uploaded successfully!", "success");
            setOpenUploadDialog(false);
            setSelectedFile(null);
            await loadChildren(page + 1, limit);
        }
        setLoading(false);
    };

    const handleEdit = async (child) => {
        setSelectedChild(child);
        const response = await getSitemapContent(child._id);

        if (response.xmlContent) {
            setEditContent(response.xmlContent);
        } else {
            setEditContent(constructXMLFromChild(child));
        }
        setOpenEditDialog(true);
    };

    const constructXMLFromChild = (child) => {
        let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
        xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
        xml += "  <url>\n";
        xml += `    <loc>${child.loc || ""}</loc>\n`;
        if (child.lastmod) xml += `    <lastmod>${child.lastmod}</lastmod>\n`;
        if (child.changefreq) xml += `    <changefreq>${child.changefreq}</changefreq>\n`;
        if (child.priority) xml += `    <priority>${child.priority}</priority>\n`;
        xml += "  </url>\n";
        xml += "</urlset>";
        return xml;
    };

    const parseXMLToUpdateData = (xmlContent) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlContent, "text/xml");

        const parserError = xmlDoc.getElementsByTagName("parsererror");
        if (parserError.length > 0) {
            throw new Error("Invalid XML format");
        }

        const url = xmlDoc.getElementsByTagName("url")[0];
        if (!url) {
            throw new Error("No URL found in XML");
        }

        return {
            loc: url.getElementsByTagName("loc")[0]?.textContent || "",
            changefreq: url.getElementsByTagName("changefreq")[0]?.textContent || "",
            lastmod: url.getElementsByTagName("lastmod")[0]?.textContent || "",
            priority: url.getElementsByTagName("priority")[0]?.textContent || "",
            updatedAt: new Date().toISOString(),
        };
    };

    const handleUpdate = async () => {
        if (!editContent.trim()) {
            showSnackbar("Sitemap content cannot be empty", "error");
            return;
        }

        setLoading(true);
        try {
            const updateData = parseXMLToUpdateData(editContent);
            const response = await updateSiteMapping(selectedChild._id, updateData);

            if (response.error) {
                showSnackbar(response.error, "error");
            } else {
                showSnackbar("URL updated successfully!", "success");
                setOpenEditDialog(false);
                setSelectedChild(null);
                setEditContent("");
                await loadChildren(page + 1, limit);
            }
        } catch (error) {
            showSnackbar(error.message || "Error updating URL", "error");
        }
        setLoading(false);
    };

    const handleDeleteClick = (child) => {
        setSelectedChild(child);
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        setOpenDeleteDialog(false);
        setLoading(true);
        const response = await deleteChildSiteMapping(selectedChild._id);

        if (response.error) {
            showSnackbar(response.error, "error");
        } else {
            showSnackbar("URL deleted successfully!", "success");
            await loadChildren(page + 1, limit);
        }
        setLoading(false);
        setSelectedChild(null);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((s) => ({ ...s, open: false }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString();
    };

    if (!sitemapUrl) {
        return (
            <div className="sitemap-container">
                <div className="no-data">
                    No sitemap selected.{" "}
                    <Button onClick={() => navigate("/dashboard/sitemap")}>Go back</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="sitemap-container">
            <div className="sitemap-breadcrumb">
                <IconButton
                    onClick={() => navigate("/dashboard/sitemap")}
                    size="small"
                    className="back-button"
                >
                    <ArrowBackIcon />
                </IconButton>
                <span>Sitemaps</span>{" "}
                <span className="sitemap-breadcrumb-sep">/</span>{" "}
                <span className="sitemap-breadcrumb-current">
                    {sourceFileLabel || "Sitemap URLs"}
                </span>
            </div>

            <div className="sitemap-header-container">
                <div>
                    <span className="sitemap-header">URLs in this sitemap</span>
                    <div className="parent-info-box">
                        <a href={sitemapUrl} target="_blank" rel="noopener noreferrer">
                            {sitemapUrl}
                        </a>
                    </div>
                </div>
                <div className="btn-container">
                    <Button
                        variant="outlined"
                        onClick={() => loadChildren(page + 1, limit)}
                        startIcon={<RefreshIcon />}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        className="bordered-button"
                        onClick={() => setOpenUploadDialog(true)}
                        startIcon={<UploadFileIcon />}
                        disabled={loading}
                    >
                        Upload Sitemap
                    </Button>
                </div>
            </div>

            <TableContainer component={Paper} className="sitemap-table-container">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell className="table-header">URL</TableCell>
                            <TableCell className="table-header">Change Frequency</TableCell>
                            <TableCell className="table-header">Last Modified</TableCell>
                            <TableCell className="table-header">Priority</TableCell>
                            <TableCell className="table-header">Updated At</TableCell>
                            <TableCell className="table-header">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && children.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" className="no-data">
                                    <CircularProgress size={40} />
                                    <div style={{ marginTop: 10 }}>Loading URLs...</div>
                                </TableCell>
                            </TableRow>
                        ) : children.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" className="no-data">
                                    No URLs found for this sitemap.
                                </TableCell>
                            </TableRow>
                        ) : (
                            children.map((child) => (
                                <TableRow key={child._id}>
                                    <TableCell className="sitemap-loc">
                                        <a href={child.loc} target="_blank" rel="noopener noreferrer">
                                            {child.loc}
                                        </a>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={child.changefreq || "N/A"}
                                            className={`changefreq-badge ${child.changefreq}`}
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(child.lastmod)}</TableCell>
                                    <TableCell>{child.priority || "N/A"}</TableCell>
                                    <TableCell>{formatDate(child.updatedAt)}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            className="action-button"
                                            disabled={loading}
                                            onClick={() => handleEdit(child)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            size="small"
                                            className="action-button"
                                            disabled={loading}
                                            onClick={() => handleDeleteClick(child)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={limit}
                    onRowsPerPageChange={(e) => {
                        setLimit(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                    sx={{
                        "& .MuiTablePagination-select": {
                            paddingRight: "28px !important",
                            paddingLeft: "8px",
                            backgroundColor: "#f0f7ff",
                            borderRadius: "6px",
                            border: "1px solid #bbdefb",
                            color: "#1565c0",
                            fontWeight: 500,
                            fontSize: 13,
                            minWidth: 40,
                        },
                        "& .MuiTablePagination-selectIcon": {
                            color: "#1565c0",
                            right: 4,
                        },
                    }}
                />
            </TableContainer>

            {/* Upload Dialog */}
            <Dialog
                open={openUploadDialog}
                onClose={() => setOpenUploadDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Upload Sitemap XML File</DialogTitle>
                <DialogContent>
                    <div className="upload-dialog-content">
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            className="upload-button"
                        >
                            Choose XML File
                            <input
                                type="file"
                                hidden
                                accept=".xml,.xml.gz"
                                onChange={handleFileUpload}
                            />
                        </Button>

                        {selectedFile && (
                            <div className="selected-file">
                                <strong>Selected file:</strong> {selectedFile.name}
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUploadDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleUploadSubmit}
                        variant="contained"
                        color="primary"
                        disabled={loading || !selectedFile}
                    >
                        {loading ? <CircularProgress size={24} /> : "Upload"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                open={openEditDialog}
                onClose={() => setOpenEditDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Edit URL: {selectedChild?.loc}</DialogTitle>
                <DialogContent>
                    <div className="edit-dialog-content">
                        <TextField
                            label="Sitemap XML Content"
                            multiline
                            rows={20}
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            fullWidth
                            variant="outlined"
                            className="xml-editor"
                            placeholder="Paste your XML content here..."
                            disabled={loading}
                        />
                        <div className="xml-hint">
                            <small>
                                Note: Keep the XML structure valid. Only one URL entry is
                                supported per update.
                            </small>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleUpdate}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : "Update URL"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{selectedChild?.loc}</strong>?
                        <br />
                        <br />
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ChildSitemap;